import styled, { x, css } from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { PlayButton } from '~/ui/buttons';
import { Card } from '~/ui/card/Card';
import { AudioPlayer } from '~/ui/video-player/AudioPlayer';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { AudioProgress } from './AudioProgress';
import { SpeakerIcon } from './SpeakerIcon';
import { TickMarkIcon } from './TickMarkIcon';
import { usePopup } from './usePopup';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useCanControl } from '~/ui/video-player/useCanControl';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { drillAnimation } from '~/ui/animation';
import { isMobile } from 'react-device-detect';
import { TrainingState, useTrainingStore } from '~/training/state';
import { t } from 'ttag';
import closeIcon from '~/assets/icons/close.svg';
import { useEditorStore } from '~/shell/Store';
import NarrationPopup from '~/editor/narration/NarrationPopup';

const selector = (s: MediaPlayerStore) =>
  select(s, 'loadMedia', 'status', 'toggle', 'play', 'loadSubtitles', 'pause');

const seqSelector = (s: SequenceStore) =>
  select(s, 'setAudioPlayed', 'addBlocker', 'removeBlocker');

interface DrillBoxProps {
  btnSize?: number;
}

const DrillBoxAnimation = styled.div<DrillBoxProps>`
  width: ${p => (p?.btnSize ? `${p?.btnSize}px` : '85px')};
  height: ${p => (p?.btnSize ? `${p?.btnSize}px` : '85px')};
  animation: ${drillAnimation} 2.3s ease infinite;
  position: absolute;
  background-color: accentDark;
  border-radius: 50%;
  opacity: 0.5;
  z-index: 1;
`;

const NumberIcon = styled.p`
  font-size: ${p => (p.children === '+' ? 40 : 24)}px;
  font-weight: 700;
  display: block;
  margin-top: ${p => (p.children === '+' ? 1 : -3)}px;
  line-height: 20px;
`;

const CloseIcon = styled(closeIcon)`
  transform: rotate(180deg);
  color: ${p => p.theme.colors.black};
  width: 14px;
  height: 14px;
`;

const CloseButton = styled.button`
  position: absolute;
  z-index: 1;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 2px solid #000;
  border-radius: 4px; /* square with subtle rounding */
  padding: 0;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const selectorVisualFeedback = (s: TrainingState) =>
  select(s, 'visualFeedback', 'setVisualFeedback');

const PopupContent = styled.div<{
  isScrollEnabled?: boolean;
  gridArea?: string;
}>`
  ${p =>
    p.isScrollEnabled &&
    css`
      max-height: 200px;
      overflow-y: scroll;
    `}
`;

export const PopupButton = ({
  mediaUrl,
  id,
  name,
  body,
  blocking,
  subtitlesUrl,
  title,
  replay,
  isPopupCentered,
  shouldShow = true,
  btnSize = 85,
  scroll = false,
  parentId,
  editable = true
}: {
  mediaUrl?: string;
  id: string;
  name: string;
  body: string;
  shouldShow: boolean;
  blocking: boolean;
  subtitlesUrl?: string;
  title?: number | string;
  replay?: boolean;
  isPopupCentered?: boolean;
  btnSize?: number;
  scroll?: boolean;
  parentId?: string;
  editable?: boolean;
}) => {
  const { contentEditable, updateFile } = useEditorStore();
  const [showAudioPopup, setShowAudioPopup] = useState(false);

  const { open, setOpen, popupRef } = usePopup();
  const { loadMedia, status, toggle, play, loadSubtitles, pause } =
    useMediaPlayerStore(selector, shallow);
  const ref = useRef<HTMLDivElement>();
  const [top, setTop] = useState<number>();
  const [left, setLeft] = useState<number>();
  const [isClicked, setIsClicked] = useState<boolean>(false);
  const { addBlocker, removeBlocker, setAudioPlayed } = useSequenceStore(
    seqSelector,
    shallow
  );

  const { visualFeedback, setVisualFeedback } = useTrainingStore(
    selectorVisualFeedback
  );

  useGlobalMedia();

  const globalMedia = useGlobalMediaStore();
  const globalPlayer = globalMedia?.player;

  useEffect(() => {
    if (globalPlayer && globalPlayer.pause) {
      try {
        globalPlayer.pause();
      } catch (e) {
        // Ignore errors when pausing uninitialized player
      }
    }
  }, []);

  // Ensure visual feedback (pulse) is enabled when this popup is gated
  useEffect(() => {
    if (
      blocking &&
      !visualFeedback &&
      typeof setVisualFeedback === 'function'
    ) {
      setVisualFeedback(true);
    }
  }, [blocking]);

  useEffect(() => {
    if (!mediaUrl) {
      addBlocker(id);
    }

    return () => {
      removeBlocker(id);
    };
  }, []);

  useEffect(() => {
    if (!blocking) return;

    if (mediaUrl) addBlocker(id);

    if (
      (mediaUrl && (status === 'nomedia' || status === 'done')) ||
      (!mediaUrl && open)
    ) {
      removeBlocker(id);
    }
  }, [status, open, mediaUrl]);

  useEffect(() => {
    if (!open) return;
    function updatePosition() {
      const rect = ref?.current?.getBoundingClientRect();
      const popupRect = popupRef?.current?.getBoundingClientRect();
      if (!rect || !popupRect) return;
      setTimeout(() => {
        if (isMobile) {
          setTop(window.innerHeight / 2 - 100);
          setLeft((window.innerWidth - 350) / 2);
        }
        if (isPopupCentered) {
          setTop(window.innerHeight / 2 - popupRect.height / 2);
          setLeft((window.innerWidth - 350) / 2);
        } else {
          setTop(rect.bottom);
          setLeft(rect.left + rect.width / 2 - popupRect.width / 2);
        }
      });
    }
    window.addEventListener('resize', updatePosition);
    updatePosition();
    return () => window.removeEventListener('resize', updatePosition);
  }, [ref.current, popupRef.current, open]);

  useEffect(() => {
    if (!mediaUrl) return;
    loadMedia(mediaUrl, subtitlesUrl, 'audio', false, 0);
    loadSubtitles(subtitlesUrl);
  }, []);

  useEffect(() => {
    if (status != 'done' && !open) {
      pause();
    }
  }, [status, open]);

  useEffect(() => {
    if (status === 'done') {
      setOpen(false);
      setAudioPlayed(id);
    }
  }, [status]);

  // Hide the button when another media player is playing
  const canControl = useCanControl() || status != 'playing';

  const clickHandler = () => {
    if (contentEditable && editable) {
      setShowAudioPopup(true);
      return;
    }

    if (globalPlayer) {
      try {
        globalPlayer.pause();
      } catch (e) {
        // Ignore errors when pausing uninitialized player
      }
    }

    if (!mediaUrl) {
      setIsClicked(true);
      removeBlocker(id);
    }

    if (status === 'done' && !replay) return;

    play();
    setOpen(true);
  };

  const [isScrollEnabled, setScrollEnabled] = useState(false);

  if (scroll) {
    useEffect(() => {
      const handleResize = () => {
        if (popupRef?.current?.clientHeight! + 36 >= window.innerHeight) {
          setScrollEnabled(true);
        }
      };

      isMobile ? setScrollEnabled(true) : setTimeout(() => handleResize(), 0);

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [open]);
  }

  const getContentForPopup = (name: string, body: string) => {
    const isDefaultAudioPointTitle =
      !mediaUrl && name?.trim() === 'Audio Point';
    const displayName = isDefaultAudioPointTitle ? '' : name;

    if (displayName && body == '' && shouldShow) {
      return (
        <x.span
          overflow="hidden"
          textOverflow="ellipsis"
          gridArea="title"
          dangerouslySetInnerHTML={{ __html: displayName }}
        />
      );
    } else {
      return (
        <PopupContent
          isScrollEnabled={isScrollEnabled || false}
          tabIndex={isScrollEnabled ? 0 : -1}
          gridArea="title"
          style={
            !mediaUrl && (!displayName || displayName.trim() === '')
              ? { paddingTop: '16px' }
              : undefined
          }
        >
          {shouldShow && (
            <>
              {displayName && (
                <x.h2
                  mb="15px"
                  dangerouslySetInnerHTML={{ __html: displayName }}
                />
              )}
              <div dangerouslySetInnerHTML={{ __html: body }} />
            </>
          )}
        </PopupContent>
      );
    }
  };

  const getStartIcon = () => {
    if (
      typeof title === 'number' ||
      (typeof title === 'string' && title !== '')
    ) {
      return <NumberIcon className="pressable">{title}</NumberIcon>;
    } else {
      return <SpeakerIcon className="pressable" />;
    }
  };

  const getBtnIcon = () => {
    if (title) {
      return <NumberIcon className="pressable">{title}</NumberIcon>;
    }

    if (isClicked && !mediaUrl) {
      return <TickMarkIcon className="pressable" />;
    }

    // For text-only popups, original UI showed a '+' button (no number)
    return (
      <x.span fontSize="40px" className="pressable">
        +
      </x.span>
    );
  };

  const escFunction = (event: React.KeyboardEvent) => {
    if (event.keyCode === 27) {
      pause();
      setOpen(false);
    }
  };

  const onSelectNarration = (audio: any) => {
    if (id && audio?.id) {
      updateFile(id.toString(), audio, 'audios', parentId);
    }

    setShowAudioPopup(false);
  };

  return (
    <x.div
      w={`${btnSize}px`}
      h={`${btnSize}px`}
      backgroundColor={
        canControl && status === 'done'
          ? 'rgba(255,255,255,0.2)'
          : 'transparent'
      }
      borderRadius="50%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      position="relative"
      zIndex={4}
      pointerEvents="auto"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      className="pressable audioPopUpBtn"
    >
      {canControl && (status !== 'done' || replay) && visualFeedback && (
        <DrillBoxAnimation className={'drillBoxEffect'} btnSize={btnSize} />
      )}
      {open &&
        status == 'playing' &&
        ReactDOM.createPortal(
          <>
            <MediaCaptions />
          </>,
          document.getElementById('root')!
        )}
      {canControl && (
        <x.button
          onClick={clickHandler}
          backgroundColor="white"
          color="black"
          borderRadius="50%"
          w={`${btnSize - 18}px`}
          h={`${btnSize - 18}px`}
          display="flex"
          justifyContent="center"
          alignItems="center"
          overflow="hidden"
          zIndex={2}
          aria-label={t`Play the audio`}
          className="pressable"
        >
          {mediaUrl ? (
            status === 'done' ? (
              <TickMarkIcon className="pressable" />
            ) : (
              getStartIcon()
            )
          ) : (
            getBtnIcon()
          )}
        </x.button>
      )}
      <AudioPlayer />
      {open &&
        ReactDOM.createPortal(
          <Card
            ref={popupRef}
            position="fixed"
            w="350px"
            p="md"
            onClick={e => e.stopPropagation()}
            top={`${top}px`}
            left={`${left}px`}
            display="grid"
            gridTemplateColumns="auto 1fr"
            gridTemplateRows="auto auto"
            gridTemplateAreas="'button title' 'button progress'"
            paddingTop={mediaUrl ? '16px' : '40px'}
            gap={mediaUrl ? 'md' : 'md'}
            onKeyDown={escFunction}
          >
            {!mediaUrl && (
              <CloseButton
                onClick={() => setOpen(false)}
                aria-label={t`close`}
                role="button"
                autoFocus
              >
                <CloseIcon />
              </CloseButton>
            )}
            {getContentForPopup(name, body)}
            {mediaUrl && (
              <>
                <x.div gridArea="button">
                  <PlayButton
                    state={status === 'playing' ? 'playing' : 'paused'}
                    autoFocus
                    onClick={toggle}
                  />
                </x.div>
                <x.div gridArea="progress">
                  <AudioProgress />
                </x.div>
              </>
            )}
          </Card>,
          document.getElementById('root')!
        )}
      {showAudioPopup &&
        contentEditable &&
        ReactDOM.createPortal(
          <NarrationPopup
            onClose={() => setShowAudioPopup(false)}
            skipUpdate={true}
            onSelectCallback={onSelectNarration}
            id={id}
            skipSequenceNarration={true}
          />,
          document.body
        )}
    </x.div>
  );
};
